if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.onThumbnailClick = this.onThumbnailClick.bind(this);
        this.onMobilePointerDown = this.onMobilePointerDown.bind(this);
        this.onMobilePointerUp = this.onMobilePointerUp.bind(this);
        this.mobileIndex = 0;
        this.mobilePointerStartX = null;
        this.mobileLoadRequest = 0;
      }

      connectedCallback() {
        /*
         * Keep product media navigation usable even when the theme's Swiper is
         * initialized late (or rebuilt after a variant change).  This listener
         * lives on the gallery itself, so newly-rendered thumbnail buttons do
         * not need to be rebound one by one.
         */
        this.addEventListener('click', this.onThumbnailClick, true);
        this.mobileStage = this.querySelector('.nuvemora-mobile-gallery__stage');
        this.mobileStage?.addEventListener('pointerdown', this.onMobilePointerDown, { passive: true });
        this.mobileStage?.addEventListener('pointerup', this.onMobilePointerUp, { passive: true });
        this.mobileStage?.addEventListener('pointercancel', this.onMobilePointerUp, { passive: true });
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onThumbnailClick, true);
        this.mobileStage?.removeEventListener('pointerdown', this.onMobilePointerDown);
        this.mobileStage?.removeEventListener('pointerup', this.onMobilePointerUp);
        this.mobileStage?.removeEventListener('pointercancel', this.onMobilePointerUp);
      }

      onThumbnailClick(event) {
        const mobileGallery = event.target.closest('[data-nuvemora-mobile-gallery]');
        if (mobileGallery && this.contains(mobileGallery)) {
          const mobileThumb = event.target.closest('[data-mobile-gallery-thumb]');
          const previous = event.target.closest('[data-mobile-gallery-prev]');
          const next = event.target.closest('[data-mobile-gallery-next]');
          const open = event.target.closest('[data-mobile-gallery-open]');

          if (mobileThumb || previous || next || open) {
            event.preventDefault();
            event.stopPropagation();
          }

          if (mobileThumb) {
            this.showMobileMedia(Number(mobileThumb.dataset.mobileGalleryThumb));
            return;
          }

          if (previous) {
            this.showMobileMedia(this.mobileIndex - 1);
            return;
          }

          if (next) {
            this.showMobileMedia(this.mobileIndex + 1);
            return;
          }

          if (open) {
            if (Date.now() - (this.mobileSwipeEndedAt || 0) < 350) return;
            const zoomDialog = this.querySelector('zoom-dialog');
            if (zoomDialog && typeof zoomDialog.open === 'function') {
              zoomDialog.open(this.mobileIndex, event);
            }
            return;
          }
        }

        const thumbnail = event.target.closest('.swiper-controls__thumbnail');
        if (!thumbnail || !this.contains(thumbnail)) return;

        event.preventDefault();
        event.stopPropagation();

        const fallbackIndex = Array.from(
          this.querySelectorAll('.swiper-controls__thumbnail')
        ).indexOf(thumbnail);
        const index = Number(thumbnail.dataset.index ?? fallbackIndex);
        if (Number.isNaN(index) || index < 0) return;

        this.setActiveMedia(index, thumbnail);
      }

      onMobilePointerDown(event) {
        this.mobilePointerStartX = event.clientX;
      }

      onMobilePointerUp(event) {
        if (this.mobilePointerStartX === null) return;

        const movement = event.clientX - this.mobilePointerStartX;
        this.mobilePointerStartX = null;

        if (Math.abs(movement) < 45) return;

        this.mobileSwipeEndedAt = Date.now();
        this.showMobileMedia(this.mobileIndex + (movement < 0 ? 1 : -1));
      }

      showMobileMedia(requestedIndex) {
        const gallery = this.querySelector('[data-nuvemora-mobile-gallery]');
        const image = gallery?.querySelector('.nuvemora-mobile-gallery__image');
        const thumbnails = Array.from(
          gallery?.querySelectorAll('[data-mobile-gallery-thumb]') || []
        );

        if (!gallery || !image || thumbnails.length === 0) return;

        const count = thumbnails.length;
        const index = ((Number(requestedIndex) % count) + count) % count;
        const thumbnail = thumbnails[index];
        const source = thumbnail.dataset.mobileGallerySrc;
        if (!source) return;

        const finalizeSelection = () => {
          this.mobileIndex = index;
          thumbnails.forEach((button, buttonIndex) => {
            const active = buttonIndex === index;
            button.classList.toggle('is-active', active);
            button.setAttribute('aria-selected', active ? 'true' : 'false');
          });

          const counter = gallery.querySelector('[data-mobile-gallery-current]');
          if (counter) counter.textContent = String(index + 1);

          thumbnail.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        };

        const currentSource = image.currentSrc || image.src;
        if (currentSource === source || decodeURI(currentSource) === decodeURI(source)) {
          finalizeSelection();
          return;
        }

        const request = ++this.mobileLoadRequest;
        gallery.classList.add('is-loading');

        /* Keep the current product image visible until the replacement has
           loaded. A slow or blocked CDN request can therefore never leave a
           blank frame on mobile. */
        const preloader = new Image();
        preloader.decoding = 'async';
        preloader.onload = () => {
          if (request !== this.mobileLoadRequest) return;

          image.srcset = '';
          image.src = source;
          image.alt = thumbnail.dataset.mobileGalleryAlt || image.alt;
          gallery.classList.remove('is-loading');
          finalizeSelection();
        };
        preloader.onerror = () => {
          if (request !== this.mobileLoadRequest) return;
          gallery.classList.remove('is-loading');
        };
        preloader.src = source;
      }

      setActiveMedia(index, selectedThumbnail) {
        const component = this.querySelector('swiper-component');
        const main = this.querySelector('.swiper-main');
        const slides = Array.from(main?.querySelectorAll('.swiper-wrapper > .swiper-slide') || []);

        /* Variant changes call this method with an id such as
           "template--123-456789". Resolve that id to the visible slide while
           thumbnail clicks continue to use their numeric data-index. */
        let targetIndex = Number(index);
        if (Number.isNaN(targetIndex)) {
          const mediaId = String(index).split('-').pop();
          targetIndex = slides.findIndex((slide) =>
            Array.from(slide.querySelectorAll('[data-media-id]')).some(
              (media) => String(media.dataset.mediaId) === mediaId
            )
          );
        }
        if (targetIndex < 0) return;

        if (window.matchMedia('(max-width: 749px)').matches) {
          this.showMobileMedia(targetIndex);
        }

        const swiper = component?.initSwiper || main?.swiper;

        if (swiper && !swiper.destroyed) {
          if (swiper.params?.loop && typeof swiper.slideToLoop === 'function') {
            swiper.slideToLoop(targetIndex);
          } else if (typeof swiper.slideTo === 'function') {
            swiper.slideTo(targetIndex);
          }
        } else {
          /* Native fallback for slow/blocked script initialization. */
          const wrapper = main?.querySelector('.swiper-wrapper');
          const target = slides[targetIndex];

          if (wrapper && target && main) {
            const offset = target.offsetLeft;
            wrapper.style.transition = 'transform 360ms ease';
            wrapper.style.transform = `translate3d(${-offset}px, 0, 0)`;
            slides.forEach((slide, slideIndex) => {
              slide.classList.toggle('swiper-slide-active', slideIndex === targetIndex);
              slide.setAttribute('aria-hidden', slideIndex === targetIndex ? 'false' : 'true');
            });
          }
        }

        const thumbnails = Array.from(
          this.querySelectorAll('.swiper-controls__thumbnail')
        );
        thumbnails.forEach((button, buttonIndex) => {
          const active = button === selectedThumbnail || buttonIndex === targetIndex;
          button.classList.toggle('active', active);
          button.setAttribute('aria-selected', active ? 'true' : 'false');
        });

        if (selectedThumbnail instanceof Element) {
          selectedThumbnail.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        }
      }
    }
  );
}
